import { useEffect, useId, useRef, useState } from "react";
import { Button } from "../../components/ui/button.tsx";
import styles from "./issaInsights.module.scss";
import { saveAnswerFeedback } from "./issaInsightsApi.ts";
import { FEEDBACK_COMMENT_MAX, FEEDBACK_RATINGS, type FeedbackRating } from "./issaFeedback.ts";

const COMMENT_PROMPT: Record<FeedbackRating, string> = {
  1: "What went wrong?",
  2: "What would have made it better?",
  3: "What was most useful?",
};

/** How long the thank-you stays beside the choices. */
const THANKS_MS = 6000;
const THANKS_AFTER_COMMENT_MS = 2500;

type Phase = "idle" | "comment" | "thanks";

/**
 * The rating under one answer, in its utility row next to "Copy response".
 *
 * Bad, Fine and Good are always there, dimmed until the answer is hovered or
 * the row has focus, with the saved choice shown pressed; clicking another one
 * changes the rating. On the rare answers the page asks about (see
 * issaFeedback.ts) the "Rate" label becomes the "How was this answer?"
 * question with a "Not now" button. A rating is saved on the click; Bad opens
 * the comment box straight away, Fine and Good offer it as a link. A comment
 * is always optional.
 */
export function IssaAnswerFeedback({ logId, rating, ask, onRated, onDismiss }: {
  logId: number;
  /** The rating saved on the server, if any. */
  rating: FeedbackRating | null;
  /** Ask about this answer now. */
  ask: boolean;
  onRated: (logId: number, rating: FeedbackRating) => void;
  /** "Not now" on the question: the page stops asking for a while. */
  onDismiss: () => void;
}) {
  const [phase, setPhase] = useState<Phase>("idle");
  const [selected, setSelected] = useState<FeedbackRating | null>(rating);
  const [dismissed, setDismissed] = useState(false);
  const [comment, setComment] = useState("");
  const [commentSent, setCommentSent] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const groupRef = useRef<HTMLSpanElement>(null);
  const commentRef = useRef<HTMLTextAreaElement>(null);
  const restoreFocus = useRef(false);
  const id = useId();

  // The question shows while the page asks, until it is answered or put off.
  const asking = ask && !dismissed && selected === null && phase === "idle";

  useEffect(() => {
    if (!saving) setSelected(rating);
    // Only a rating reloaded from the server is adopted, never one mid-save.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rating]);

  useEffect(() => {
    if (phase === "comment") commentRef.current?.focus();
    if (phase === "idle" && restoreFocus.current) {
      // The control that had focus is gone: hand it to the choice that was made.
      restoreFocus.current = false;
      groupRef.current?.querySelector<HTMLButtonElement>('[aria-pressed="true"]')?.focus();
    }
  }, [phase]);

  useEffect(() => {
    if (phase !== "thanks") return;
    const timer = window.setTimeout(() => backToIdle(), commentSent ? THANKS_AFTER_COMMENT_MS : THANKS_MS);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, commentSent]);

  /** Removes the thank-you or the comment box, keeping keyboard focus in the row. */
  function backToIdle() {
    const active = document.activeElement;
    restoreFocus.current = !!active && !!rootRef.current?.contains(active) && !groupRef.current?.contains(active);
    setError(null);
    setPhase("idle");
  }

  async function rate(value: FeedbackRating) {
    const previous = selected;
    setSelected(value);
    setSaving(true);
    setError(null);
    try {
      await saveAnswerFeedback(logId, value);
      onRated(logId, value);
      setCommentSent(false);
      setComment("");
      setPhase(value === 1 ? "comment" : "thanks");
    } catch {
      setSelected(previous);
      setError("Couldn't send that. Try again.");
    } finally {
      setSaving(false);
    }
  }

  async function sendComment() {
    const text = comment.trim();
    if (!text || !selected) return;
    setSaving(true);
    setError(null);
    try {
      await saveAnswerFeedback(logId, selected, text);
      setCommentSent(true);
      setPhase("thanks");
    } catch {
      setError("Couldn't send your comment. Try again.");
    } finally {
      setSaving(false);
    }
  }

  function dismiss() {
    setDismissed(true);
    onDismiss();
  }

  const labelId = `${id}-label`;
  return (
    // display: contents, so the choices sit in the answer's utility row and the
    // comment box can wrap onto a row of its own below it.
    <div ref={rootRef} className={styles.feedbackWrap}>
      <span
        ref={groupRef}
        className={`${styles.feedback} ${asking ? styles.feedbackAsk : styles.feedbackQuiet}`}
        role="group"
        aria-labelledby={labelId}
      >
        <span id={labelId} className={styles.feedbackQuestion}>
          {asking ? "How was this answer?" : selected ? "Rated" : "Rate"}
        </span>
        {FEEDBACK_RATINGS.map((r) => (
          <button
            key={r.value}
            type="button"
            className={styles.feedbackOption}
            aria-pressed={selected === r.value}
            disabled={saving}
            onClick={() => { if (selected !== r.value || phase !== "idle") void rate(r.value); }}
          >
            {r.label}
          </button>
        ))}
        {asking && (
          <button type="button" className={styles.feedbackDismiss} aria-label="Not now" title="Not now" onClick={dismiss}>
            <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        )}
        {phase === "thanks" && (
          <span className={styles.feedbackThanks}>
            Thanks for the feedback.
            {!commentSent && (
              <>
                {" "}
                <button type="button" className={styles.feedbackLink} onClick={() => setPhase("comment")}>
                  Add a comment
                </button>
              </>
            )}
          </span>
        )}
        {error && phase !== "comment" && <span className={styles.feedbackError} role="alert">{error}</span>}
      </span>

      {/* A row of its own under the choices: one small box with the actions
          inside it, so it reads as part of the answer's footer, not a second chat box. */}
      {phase === "comment" && selected && (
        <form
          className={styles.feedbackComment}
          onSubmit={(event) => {
            event.preventDefault();
            void sendComment();
          }}
        >
          <div className={styles.feedbackCommentBox}>
            <label className={styles.srOnly} htmlFor={`${id}-comment`}>{COMMENT_PROMPT[selected]}</label>
            <textarea
              ref={commentRef}
              id={`${id}-comment`}
              className={styles.feedbackCommentInput}
              rows={1}
              maxLength={FEEDBACK_COMMENT_MAX}
              placeholder={`${COMMENT_PROMPT[selected]} (optional)`}
              value={comment}
              onChange={(event) => {
                setComment(event.target.value);
                // Grows with the text; the stylesheet caps it and scrolls beyond.
                event.target.style.height = "auto";
                event.target.style.height = `${event.target.scrollHeight}px`;
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
                  event.preventDefault();
                  void sendComment();
                } else if (event.key === "Escape") {
                  event.preventDefault();
                  event.stopPropagation();
                  setPhase("thanks");
                }
              }}
            />
            <span className={styles.feedbackCommentActions}>
              <button type="button" className={styles.feedbackSkip} onClick={() => setPhase("thanks")}>Skip</button>
              <Button type="submit" size="sm" loading={saving} disabled={!comment.trim()}>Send</Button>
            </span>
          </div>
          {error && <span className={styles.feedbackError} role="alert">{error}</span>}
        </form>
      )}
      {/* Mounted with the choices, so the thank-you is announced when it appears. */}
      <span className={styles.srOnly} role="status">{phase === "thanks" ? "Thanks for the feedback." : ""}</span>
    </div>
  );
}
